'use client';

import * as React from 'react';
import styled, { css } from 'styled-components';
import { parse, intlFormat, format } from 'date-fns';
import { IconChild } from '@lmn/lastminute-react-icons/lastminute-icons-solid.js';
import { IconBed, IconMoon, IconUser } from '@/app/common/icons/libraryIcons';
import { type IntlDateTimeFormatOptions, SERVICES_DATES_FORMAT } from '@/app/components/hotel-detail/common/dates';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';

import customCss from '@/app/components/hotel-detail/css/customCss';
import { Placeholder } from '@lmn/design-system';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useUrlSearchParams } from './UrlSearchParamsProvider';

export type SearchSummaryProps = {
  useRoomDates: boolean;
} & React.HTMLAttributes<HTMLElement>;

// TODO Use locales to format dates
function getTripDates(params: {
  checkin: string | null | undefined;
  checkout: string | null | undefined;
  locale: string;
  dateFormat?: string;
  intlDateFormat?: IntlDateTimeFormatOptions;
}): { checkinFormatted: string; checkoutFormatted: string } | undefined {
  const { checkin, checkout, locale, dateFormat, intlDateFormat } = params;
  if (checkin == null || checkout == null) return;
  const checkinDate = parse(checkin, SERVICES_DATES_FORMAT, new Date());
  const checkoutDate = parse(checkout, SERVICES_DATES_FORMAT, new Date());
  const checkinFormatted = intlDateFormat
    ? intlFormat(checkinDate, intlDateFormat, { locale })
    : dateFormat
      ? format(checkinDate, dateFormat)
      : format(checkinDate, 'dd/mm/yyyy');
  const checkoutFormatted = intlDateFormat
    ? intlFormat(checkoutDate, intlDateFormat, { locale })
    : dateFormat
      ? format(checkoutDate, dateFormat)
      : format(checkoutDate, 'dd/mm/yyyy');
  // eslint-disable-next-line consistent-return
  return { checkinFormatted, checkoutFormatted };
}

const SummaryItem = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.5rem;
  margin: 0.25rem 0;
  line-height: 1;
  border-right: 1px solid ${(p) => p.theme.kallisto.color.border.static.borderDisabled};

  &:last-of-type {
    border-right: none;
  }

  @media ${customCss.breakpoints.sm} {
    padding: 0 0.75rem;
  }

  @media ${customCss.breakpoints.xl} {
    &:first-child {
      padding-left: 0;
    }

    &:last-child {
      padding-right: 0;
    }
  }
`;

type OccupancyIconsProps = {
  occupancy?: {
    adults: number;
    children: number;
    rooms: number;
  };
  nights: number;
} & React.HTMLAttributes<HTMLElement>;

function OccupancyIcons(props: OccupancyIconsProps) {
  const { occupancy, nights, ...rest } = props;

  return occupancy ? (
    <div {...rest}>
      <SummaryItem data-testid="summary-nights">
        <IconMoon
          style={{
            marginRight: '0.375rem',
          }}
        />
        {nights}
      </SummaryItem>
      <SummaryItem>
        <IconUser
          style={{
            marginRight: '0.375rem',
          }}
        />
        {occupancy.adults}
      </SummaryItem>
      {occupancy.children > 0 && (
        <SummaryItem>
          <IconChild
            style={{
              marginRight: '0.375rem',
            }}
          />
          {occupancy.children}
        </SummaryItem>
      )}
      <SummaryItem>
        <IconBed
          style={{
            marginRight: '0.375rem',
          }}
        />
        {occupancy.rooms}
      </SummaryItem>
    </div>
  ) : null;
}

export function SearchSummaryLoading(props: React.HTMLAttributes<HTMLElement>) {
  return (
    <div {...props}>
      <Placeholder height="20px" width="340px" />
    </div>
  );
}

export function useSearchSummary(props: {
  useRoomDates: boolean;
  dateFormat?: string;
  intlDateFormat?: IntlDateTimeFormatOptions;
}) {
  const { useRoomDates, dateFormat, intlDateFormat } = props;

  const { localization } = usePageWrapperData();
  const {
    checkin: urlCheckin,
    checkout: urlCheckout,
    nights: urlNights,
    adults,
    children,
    totalRooms,
  } = useUrlSearchParams();
  const { offersData } = useOffersContext();
  const { locale } = localization;

  const checkin = (useRoomDates && offersData?.checkin) || urlCheckin;
  const checkout = (useRoomDates && offersData?.checkout) || urlCheckout;
  const nights = offersData?.nights || urlNights;
  // Sometimes the dates of the trip are more that the nights at the hotel in case of overnight flights
  const tripNights = urlNights;
  const tripDates = getTripDates({ checkin, checkout, locale, dateFormat, intlDateFormat });
  const occupancy = { adults, children, rooms: totalRooms };

  return {
    checkin,
    checkout,
    nights,
    tripNights,
    tripDates,
    occupancy,
  };
}

export default function SearchSummary(props: SearchSummaryProps) {
  const { useRoomDates } = props;
  const { nights, tripDates, occupancy } = useSearchSummary({
    useRoomDates,
    intlDateFormat: { weekday: 'short', day: 'numeric', month: 'short' },
  });

  if (!nights || !tripDates) return <SearchSummaryLoading />;

  return (
    <>
      <SummaryItem
        css={css`
          width: 100%;
          @media ${customCss.breakpoints.sm} {
            width: unset;
          }
        `}
      >
        {tripDates.checkinFormatted} - {tripDates.checkoutFormatted}
      </SummaryItem>
      <OccupancyIcons nights={nights} occupancy={occupancy} />
    </>
  );
}
